/**
 * Builds multi-series line specs for the interactive PMC / periodic chart.
 * Ported from Ai-Tempo PeriodicChart.tsx.
 */
import {
  BLUE, BLUEL, PINK, PINKL, GREEN, GREENL, CYAN, GOLD, EMBER, VIOLET, PMC_COLORS, CATEGORICAL_COLORS, seriesStrokeDash,
} from './palette';
import type { PmcChartSettings } from './pmcSettings';

export type RawPoint = {
  x: number;
  y: number;
  startDate: string;
  endDate: string;
  workoutId?: number;
};

export type SeriesKind = 'line' | 'dotline' | 'dots' | 'bar';

export type LineSpec = {
  label: string;
  color: string;
  color2?: string;
  points: RawPoint[];
  dashed?: boolean;
  dashArray?: string;
  kind: SeriesKind;
  axis: 'left' | 'right' | 'if';
  peakMetric?: string;
};

const POWER_PEAK_PALETTE = [...CATEGORICAL_COLORS];
const HR_PEAK_PALETTE = [...CATEGORICAL_COLORS];

const sortByX = (a: { x: number }, b: { x: number }) => a.x - b.x;

const formatDateValue = (value?: string | Date | null): string => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : '';
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const parseWorkoutId = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const id = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

const formatPeakInterval = (seconds: number): string =>
  seconds >= 60 ? `${Math.round(seconds / 60)} min` : `${seconds}s`;

const buildPeakLines = (
  rawArray: unknown,
  fallbackName: string,
  prettyMap: (raw: string) => string,
  palette: string[],
  peakMetric: string,
): LineSpec[] => {
  if (!Array.isArray(rawArray) || !rawArray.length) return [];
  return rawArray
    .map((peakGroup: Record<string, unknown>, idx: number) => {
      const points: RawPoint[] = ((peakGroup?.points || []) as Record<string, unknown>[])
        .map((pt) => {
          const workout = pt?.workout as Record<string, unknown> | undefined;
          const dateVal = pt?.record_date ?? workout?.workout_day;
          if (!dateVal) return null;
          const yVal = pt?.value ?? pt?.peak_value ?? 0;
          const yNum = typeof yVal === 'number' ? yVal : parseFloat(String(yVal)) || 0;
          const startDate = formatDateValue(String(dateVal));
          const workoutId =
            parseWorkoutId(workout?.id) ??
            parseWorkoutId(pt?.workout_id);
          return {
            x: new Date(String(dateVal)).getTime(),
            y: Math.round(yNum),
            startDate,
            endDate: startDate,
            workoutId,
          } as RawPoint;
        })
        .filter(Boolean) as RawPoint[];
      points.sort(sortByX);
      const firstPoint = (peakGroup?.points as Record<string, unknown>[] | undefined)?.[0];
      const intervalSec = firstPoint?.interval != null ? Number(firstPoint.interval) : 0;
      const rawType = String(peakGroup?.peak_type ?? '');
      const prettyType = prettyMap(rawType).replace(/\s+/g, ' ').trim();
      const intervalLabel = intervalSec > 0 ? ` (${formatPeakInterval(intervalSec)})` : '';
      const typeHasInterval = /\d/.test(prettyType);
      const name =
        prettyType.length > 0
          ? typeHasInterval ? prettyType : `${prettyType}${intervalLabel}`
          : `${fallbackName}${intervalLabel}`;
      return {
        label: name,
        color: palette[idx % palette.length],
        dashArray: seriesStrokeDash(idx),
        points,
        kind: 'dotline' as SeriesKind,
        axis: 'right' as const,
        peakMetric,
      };
    })
    .filter((s) => s.points.length > 0);
};

/** Catmull-Rom spline → SVG path d string. */
export function catmullRom(pts: [number, number][]): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

/** Build visible series from API payload + chart settings. */
export function buildPeriodicLines(data: unknown, settings: PmcChartSettings): LineSpec[] {
  const d = data as Record<string, unknown> | null | undefined;
  const pmcCfg = settings.pmc ?? {};
  const out: LineSpec[] = [];

  const ctl: RawPoint[] = [];
  const atl: RawPoint[] = [];
  const tsb: RawPoint[] = [];
  const ifs: RawPoint[] = [];

  const pushFromRow = (dateVal: unknown, row: Record<string, unknown>) => {
    const t = new Date(String(dateVal)).getTime();
    if (Number.isNaN(t)) return;
    const dStr = formatDateValue(String(dateVal));
    const ctlVal = toFiniteNumber(row?.ctl);
    const atlVal = toFiniteNumber(row?.atl);
    const tsbVal = toFiniteNumber(row?.tsb);
    const ifVal = toFiniteNumber(row?.if_actual);
    if (pmcCfg.show_CTL !== false && ctlVal != null) {
      ctl.push({ x: t, y: ctlVal, startDate: dStr, endDate: dStr });
    }
    if (pmcCfg.show_ATL !== false && atlVal != null) {
      atl.push({ x: t, y: atlVal, startDate: dStr, endDate: dStr });
    }
    if (pmcCfg.show_form_TSB !== false && tsbVal != null) {
      tsb.push({ x: t, y: tsbVal, startDate: dStr, endDate: dStr });
    }
    if (pmcCfg.show_IF && ifVal != null) {
      ifs.push({ x: t, y: ifVal, startDate: dStr, endDate: dStr });
    }
  };

  if (Array.isArray(d?.pmc)) {
    (d.pmc as Record<string, unknown>[]).forEach((item) =>
      pushFromRow(item?.calculation_date, item),
    );
  }

  if (!ctl.length && !atl.length && !tsb.length) {
    const bucket =
      (d?.last_365_days as Record<string, unknown> | undefined)?.default ??
      (d?.last_90_days as Record<string, unknown> | undefined)?.default ??
      (d?.last_28_days as Record<string, unknown> | undefined)?.default ??
      (d?.last_7_days as Record<string, unknown> | undefined)?.default;
    if (bucket && typeof bucket === 'object') {
      Object.entries(bucket as Record<string, Record<string, unknown>>).forEach(([date, row]) =>
        pushFromRow(row?.date ?? date, row),
      );
    }
  }

  ctl.sort(sortByX);
  atl.sort(sortByX);
  tsb.sort(sortByX);
  ifs.sort(sortByX);

  if (ctl.length) {
    out.push({ label: 'Fitness · CTL', color: PMC_COLORS.ctl, points: ctl, kind: 'line', axis: 'left' });
  }
  if (atl.length) {
    out.push({ label: 'Fatigue · ATL', color: PMC_COLORS.atl, dashArray: seriesStrokeDash(1), points: atl, kind: 'line', axis: 'left' });
  }
  if (tsb.length) {
    out.push({
      label: 'Form · TSB',
      color: PMC_COLORS.tsbPositive,
      points: tsb,
      dashed: true,
      kind: 'line',
      axis: 'left',
    });
  }

  const wsCfg = settings.workout_summery ?? {};
  const wsType = String(wsCfg.workout_summery_types ?? 'tss_actual');
  const showWs = wsCfg.show_workout_summery !== false;
  const showTssFromPmc = pmcCfg.show_TSS !== false;
  const activeSummaryType = showWs ? wsType : (showTssFromPmc ? 'tss_actual' : null);

  const summaryBars: Record<string, RawPoint[]> = {
    tss_actual: [],
    distance: [],
    total_ascent: [],
    total_descent: [],
    work: [],
    total_time: [],
    calories: [],
    if_actual: [],
  };

  /** Daily TSS from training_metrics pmc rows (calculation_date + tss_actual). */
  const collectTssFromPmcRows = (): void => {
    if (!showTssFromPmc && activeSummaryType !== 'tss_actual') return;
    (d?.pmc as Record<string, unknown>[] | undefined)?.forEach((item) => {
      const dateVal = item?.calculation_date ?? item?.date;
      if (!dateVal) return;
      const dayX = new Date(String(dateVal)).getTime();
      if (Number.isNaN(dayX)) return;
      const tssVal = toFiniteNumber(item?.tss_actual ?? item?.tss);
      if (tssVal == null) return;
      const startDate = formatDateValue(String(dateVal));
      summaryBars.tss_actual.push({ x: dayX, y: tssVal, startDate, endDate: startDate });
    });
  };

  if (activeSummaryType && activeSummaryType !== 'tss_actual' && Array.isArray(d?.workout_summery)) {
    (d.workout_summery as Record<string, unknown>[]).forEach((item) => {
      const dayX = new Date(String(item?.workout_day)).getTime();
      if (Number.isNaN(dayX)) return;
      const startDate = formatDateValue(String(item?.workout_day));
      const make = (y: number): RawPoint => ({ x: dayX, y, startDate, endDate: startDate });

      const distVal = toFiniteNumber(item?.distance);
      if (distVal != null) summaryBars.distance.push(make(distVal / 1000));

      const ascVal = toFiniteNumber(item?.total_ascent ?? item?.total_assent);
      if (ascVal != null) summaryBars.total_ascent.push(make(ascVal / 1000));

      const descVal = toFiniteNumber(item?.total_descent ?? item?.total_secent);
      if (descVal != null) summaryBars.total_descent.push(make(descVal / 1000));

      const workVal = toFiniteNumber(item?.work);
      if (workVal != null) summaryBars.work.push(make(workVal / 1000));

      const timeVal = toFiniteNumber(item?.total_time);
      if (timeVal != null) summaryBars.total_time.push(make(timeVal / 3600));

      const calVal = toFiniteNumber(item?.calories);
      if (calVal != null) summaryBars.calories.push(make(calVal / 1000));

      const ifVal = toFiniteNumber(item?.if_actual);
      if (ifVal != null) summaryBars.if_actual.push(make(ifVal));
    });
  }

  const wantTssBars = showTssFromPmc || activeSummaryType === 'tss_actual';
  if (wantTssBars) {
    collectTssFromPmcRows();
    if (summaryBars.tss_actual.length === 0 && Array.isArray(d?.workout_summery)) {
      (d.workout_summery as Record<string, unknown>[]).forEach((item) => {
        const dayX = new Date(String(item?.workout_day)).getTime();
        if (Number.isNaN(dayX)) return;
        const startDate = formatDateValue(String(item?.workout_day));
        const tssVal = toFiniteNumber(item?.tss_actual);
        if (tssVal == null) return;
        summaryBars.tss_actual.push({ x: dayX, y: tssVal, startDate, endDate: startDate });
      });
    }
  }

  Object.values(summaryBars).forEach((arr) => arr.sort(sortByX));

  const summarySeries: Array<{ key: string; label: string; color: string; color2?: string; points: RawPoint[] }> = [
    { key: 'tss_actual', label: 'TSS', color: GOLD, color2: GOLD, points: summaryBars.tss_actual },
    { key: 'distance', label: 'Distance (km)', color: BLUE, color2: BLUEL, points: summaryBars.distance },
    { key: 'total_ascent', label: 'Ascent (km)', color: GREEN, color2: GREENL, points: summaryBars.total_ascent },
    { key: 'total_descent', label: 'Descent (km)', color: GOLD, color2: GOLD, points: summaryBars.total_descent },
    { key: 'work', label: 'Work (KJ)', color: VIOLET, points: summaryBars.work },
    { key: 'total_time', label: 'Time (h)', color: CYAN, color2: CYAN, points: summaryBars.total_time },
    { key: 'calories', label: 'Calories (KCal)', color: PINK, color2: PINKL, points: summaryBars.calories },
  ];

  if (activeSummaryType && activeSummaryType !== 'if_actual') {
    const spec = summarySeries.find((s) => s.key === activeSummaryType);
    if (spec?.points.length) {
      out.push({ label: spec.label, color: spec.color, color2: spec.color2, points: spec.points, kind: 'bar', axis: 'right' });
    }
  }

  if (activeSummaryType === 'if_actual' && summaryBars.if_actual.length) {
    ifs.push(...summaryBars.if_actual);
    ifs.sort(sortByX);
  }

  if ((pmcCfg.show_IF || activeSummaryType === 'if_actual') && ifs.some((p) => p.y !== 0)) {
    out.push({ label: 'IF', color: EMBER, points: ifs, kind: 'dots', axis: 'if' });
  }

  const metricPts: RawPoint[] = [];
  (d?.metrics as Record<string, unknown>[] | undefined)?.forEach((item) => {
    if (!item?.metric_date) return;
    const yVal = toFiniteNumber(item?.value);
    if (yVal == null) return;
    const x = new Date(String(item.metric_date)).getTime();
    const startDate = formatDateValue(String(item.metric_date));
    metricPts.push({ x, y: yVal, startDate, endDate: startDate });
  });
  metricPts.sort(sortByX);
  if (metricPts.length) {
    out.push({ label: 'Metrics', color: VIOLET, points: metricPts, kind: 'dotline', axis: 'left' });
  }

  const calPts: RawPoint[] = [];
  (d?.nutrition as Record<string, unknown>[] | undefined)?.forEach((item) => {
    const yVal = toFiniteNumber(item?.total_calories_expended);
    if (yVal == null) return;
    const x = new Date(String(item?.workout_day)).getTime();
    if (Number.isNaN(x)) return;
    const startDate = formatDateValue(String(item?.workout_day));
    calPts.push({ x, y: yVal, startDate, endDate: startDate });
  });
  calPts.sort(sortByX);
  if (calPts.length) {
    out.push({ label: 'Calories', color: PINK, color2: PINKL, points: calPts, kind: 'bar', axis: 'right' });
  }

  const peakPretty = (prefix: string | undefined) => (raw: string) => {
    const p = prefix ?? 'power';
    if (p === 'heart_rate') return raw.replace(/^heart_rate/i, 'HR ').replace(/_/g, ' ');
    if (p === 'speed') return raw.replace(/^speed/i, 'Speed ').replace(/_/g, ' ');
    if (p === 'pace') return raw.replace(/^pace/i, 'Pace ').replace(/_/g, ' ');
    if (p === 'cadence') return raw.replace(/^cadence/i, 'Cadence ').replace(/_/g, ' ');
    return raw.replace(/^power/i, 'Power ').replace(/_/g, ' ');
  };

  if (settings.best_peaks_1?.show_best_peaks) {
    out.push(
      ...buildPeakLines(
        d?.best_peaks_1,
        'Peak',
        peakPretty(settings.best_peaks_1.peak_types),
        POWER_PEAK_PALETTE,
        settings.best_peaks_1.peak_types ?? 'power',
      ),
    );
  }
  if (settings.best_peaks_2?.show_best_peaks) {
    out.push(
      ...buildPeakLines(
        d?.best_peaks_2,
        'Peak',
        peakPretty(settings.best_peaks_2.peak_types),
        HR_PEAK_PALETTE,
        settings.best_peaks_2.peak_types ?? 'heart_rate',
      ),
    );
  }

  return out;
}

export const VIEWBOX_WIDTH = 560;
export const VIEWBOX_HEIGHT = 320;
export const PAD_T = 18;
export const PAD_B = 28;
export const PAD_L = 24;
export const PAD_R = 24;
export const CHART_W = VIEWBOX_WIDTH - PAD_L - PAD_R;
export const CHART_H = VIEWBOX_HEIGHT - PAD_T - PAD_B;
